import { defineStore } from 'pinia';
import {
  evaluateLite,
  formatLite,
  isFullEngineRequired,
} from '../core/engine-lite.js';
import { runScientificTask } from '../core/scientificWorker.js';

let fullEnginePromise;

let previewRequest = 0;

function loadFullEngine() {
  fullEnginePromise ??= import('../core/engine.js');
  return fullEnginePromise;
}

function needsComplexEngine(input) {
  const expression = String(input);
  return (
    /\bi\b/i.test(expression) || /sqrt\s*\(\s*-\s*(?:\d|\.)/i.test(expression)
  );
}

function evaluateAdvanced(input, scope, options) {
  if (needsComplexEngine(input)) {
    return runScientificTask('complex', { input, scope, options });
  }
  return loadFullEngine().then((engine) =>
    engine.evaluate(input, scope, options)
  );
}

function evaluateWithFallback(input, scope, options) {
  const quick = evaluateLite(input, scope, options);
  if (!isFullEngineRequired(quick) && !needsComplexEngine(input)) return quick;
  return evaluateAdvanced(input, scope, options);
}

const HISTORY_KEY = 'novacalc.history';
const VARS_KEY = 'novacalc.vars';
const PREFS_KEY = 'novacalc.prefs';
const MEMORY_KEY = 'novacalc.memory';
const BACKUP_APP = 'novacalc';
const BACKUP_VERSION = 1;

const DEFAULT_PREFS = {
  theme: 'dark',
  scientific: false,
  angleMode: 'DEG',
  precision: 12,
  panel: null,
};

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / mode privé : on ignore silencieusement */
  }
}

function closeParentheses(expression) {
  let depth = 0;
  for (const char of expression) {
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);
  }
  return expression + ')'.repeat(depth);
}
function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function sanitizeHistory(history) {
  if (!Array.isArray(history)) return [];
  return history
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry, index) => ({
      id: finiteNumber(entry.id) ?? Date.now() + index,
      expr: String(entry.expr || '')
        .trim()
        .slice(0, 500),
      result: String(entry.result || '').slice(0, 500),
      label: String(entry.label || '')
        .trim()
        .slice(0, 60),
      ts: finiteNumber(entry.ts) ?? Date.now(),
      favorite: Boolean(entry.favorite),
    }))
    .filter((entry) => entry.expr.length > 0 && entry.result.length > 0)
    .slice(0, 250);
}

export function normalizeBackup(payload) {
  if (!payload || typeof payload !== 'object') {
    return { ok: false, error: 'Fichier de sauvegarde invalide' };
  }
  if (payload.app !== BACKUP_APP || payload.version !== BACKUP_VERSION) {
    return { ok: false, error: 'Version de sauvegarde non compatible' };
  }

  const rawPrefs =
    payload.prefs && typeof payload.prefs === 'object' ? payload.prefs : {};
  const variables = {};
  if (
    payload.variables &&
    typeof payload.variables === 'object' &&
    !Array.isArray(payload.variables)
  ) {
    Object.entries(payload.variables).forEach(([name, value]) => {
      if (
        /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) &&
        finiteNumber(value) !== null
      ) {
        variables[name] = value;
      }
    });
  }

  return {
    ok: true,
    data: {
      history: sanitizeHistory(payload.history),
      variables,
      prefs: {
        ...DEFAULT_PREFS,
        theme: rawPrefs.theme === 'light' ? 'light' : 'dark',
        scientific: Boolean(rawPrefs.scientific),
        angleMode: ['DEG', 'RAD', 'GRAD'].includes(rawPrefs.angleMode)
          ? rawPrefs.angleMode
          : 'DEG',
        precision: [8, 12, 16].includes(Number(rawPrefs.precision))
          ? Number(rawPrefs.precision)
          : 12,
        panel: ['history', 'graph'].includes(rawPrefs.panel)
          ? rawPrefs.panel
          : null,
      },
      memory: payload.memory === null ? null : finiteNumber(payload.memory),
    },
  };
}

export const useCalculator = defineStore('calculator', {
  state: () => ({
    // Saisie courante (ce que voit l'utilisateur dans l'écran)
    input: '',
    // Résultat prévisualisé en direct sous la saisie
    preview: '',
    // Dernier résultat validé
    result: '',
    // Y a-t-il une erreur active ?
    error: '',
    // Historique persistant : [{ id, expr, result, ts, favorite }]
    history: load(HISTORY_KEY, []),
    // Variables utilisateur { x: 5 }
    variables: load(VARS_KEY, {}),
    // Préférences UI
    prefs: load(PREFS_KEY, DEFAULT_PREFS),
    // Signal one-shot pour déclencher une animation a chaque calcul validé
    lastComputeAt: 0,
    lastValue: null,
    memory: load(MEMORY_KEY, null),
  }),

  getters: {
    theme: (s) => s.prefs.theme,
    isScientific: (s) => s.prefs.scientific,
    angleMode: (s) => s.prefs.angleMode || 'DEG',
    precision: (s) =>
      [8, 12, 16].includes(Number(s.prefs.precision))
        ? Number(s.prefs.precision)
        : 12,
    hasMemory: (s) => s.memory !== null,
    activePanel: (s) => s.prefs.panel,
    hasInput: (s) => s.input.trim().length > 0,
    // Ecran affiche : priorité au résultat validé, sinon la saisie, sinon 0.
    display: (s) => s.input || s.result || '0',
  },

  actions: {
    // --- Saisie ---
    append(token) {
      this.error = '';
      // Si on repart d'un résultat validé, une nouvelle saisie efface l'écran
      // (sauf si on enchaîne avec un opérateur).
      if (this.result && !this.input) {
        this.input = /^[-+×÷*/^%]/.test(token) ? this.result : '';
        this.result = '';
      }
      this.input += token;
      this.updatePreview();
    },

    setInput(value) {
      this.error = '';
      this.result = '';
      this.input = value;
      this.updatePreview();
    },

    backspace() {
      this.input = this.input.slice(0, -1);
      this.error = '';
      this.updatePreview();
    },

    clear() {
      this.input = '';
      this.preview = '';
      this.result = '';
      this.error = '';
    },

    // --- Aperçu en direct (non destructif) ---
    updatePreview() {
      const request = ++previewRequest;
      if (!this.input.trim()) {
        this.preview = '';
        return;
      }
      const options = {
        angleMode: this.angleMode,
        precision: this.precision,
        ans: this.lastValue,
      };
      const quick = evaluateLite(this.input, this.variables, options);
      if (!isFullEngineRequired(quick) && !needsComplexEngine(this.input)) {
        this.preview = quick.ok ? quick.text : '';
        return;
      }
      this.preview = '...';
      evaluateAdvanced(this.input, this.variables, options).then((res) => {
        if (request !== previewRequest) return;
        this.preview = res.ok ? res.text : '';
      });
    },

    // --- Calcul validé (=) ---
    compute() {
      if (!this.input.trim()) return;
      const expression = closeParentheses(this.input);
      const options = {
        angleMode: this.angleMode,
        precision: this.precision,
        ans: this.lastValue,
      };
      const requestedInput = this.input;
      const quick = evaluateLite(expression, this.variables, options);
      if (isFullEngineRequired(quick) || needsComplexEngine(expression)) {
        this.error = '';
        return evaluateAdvanced(expression, this.variables, options).then(
          (res) => {
            if (this.input !== requestedInput) return;
            this.commitResult(expression, res);
          }
        );
      }
      this.commitResult(expression, quick);
    },

    commitResult(expression, res) {
      if (!res.ok) {
        this.error = res.error;
        return;
      }

      const nextVariables = { ...res.scope };
      Object.keys(nextVariables).forEach((name) => {
        if (
          typeof nextVariables[name] === 'function' ||
          name === 'phi' ||
          name === 'ans'
        ) {
          delete nextVariables[name];
        }
      });
      this.variables = nextVariables;
      save(VARS_KEY, this.variables);

      const text = res.text;
      this.pushHistory(expression, text);
      this.result = text;
      this.lastValue = res.value;
      this.input = '';
      this.preview = '';
      this.error = '';
      this.lastComputeAt = Date.now();
    },

    // --- Historique ---
    pushHistory(expr, result) {
      this.history.unshift({
        id: Date.now() + Math.random(),
        expr,
        result,
        label: '',
        ts: Date.now(),
        favorite: false,
      });
      // Garde les 200 dernières entrées (hors favoris).
      if (this.history.length > 200) {
        const favorites = this.history.filter((h) => h.favorite);
        this.history = [...this.history.slice(0, 200), ...favorites].slice(
          0,
          250
        );
      }
      save(HISTORY_KEY, this.history);
    },

    reuse(entry) {
      // Réinjecte un résultat passé comme point de départ.
      this.setInput(entry.result);
    },

    reuseExpression(entry) {
      this.setInput(entry.expr);
    },

    toggleFavorite(id) {
      const entry = this.history.find((h) => h.id === id);
      if (entry) {
        entry.favorite = !entry.favorite;
        save(HISTORY_KEY, this.history);
      }
    },

    renameHistory(id, label) {
      const entry = this.history.find((h) => h.id === id);
      if (!entry) return;
      entry.label = String(label || '')
        .trim()
        .slice(0, 60);
      save(HISTORY_KEY, this.history);
    },

    removeHistory(id) {
      this.history = this.history.filter((entry) => entry.id !== id);
      save(HISTORY_KEY, this.history);
    },

    clearHistory() {
      this.history = this.history.filter((h) => h.favorite);
      save(HISTORY_KEY, this.history);
    },

    createBackup() {
      return {
        app: BACKUP_APP,
        version: BACKUP_VERSION,
        exportedAt: new Date().toISOString(),
        history: this.history,
        variables: this.variables,
        prefs: this.prefs,
        memory: this.memory,
      };
    },

    restoreBackup(payload) {
      const normalized = normalizeBackup(payload);
      if (!normalized.ok) return normalized;

      this.history = normalized.data.history;
      this.variables = normalized.data.variables;
      this.prefs = normalized.data.prefs;
      this.memory = normalized.data.memory;
      this.input = '';
      this.preview = '';
      this.result = '';
      this.error = '';
      this.lastValue = null;

      save(HISTORY_KEY, this.history);
      save(VARS_KEY, this.variables);
      save(PREFS_KEY, this.prefs);
      save(MEMORY_KEY, this.memory);
      return { ok: true, count: this.history.length };
    },

    // --- Préférences ---
    memoryValue() {
      const candidate = this.input || this.result;
      if (!candidate) return null;
      const options = {
        angleMode: this.angleMode,
        precision: this.precision,
        ans: this.lastValue,
      };
      const result = evaluateWithFallback(candidate, this.variables, options);
      const valueOf = (res) =>
        res.ok && typeof res.value === 'number' && Number.isFinite(res.value)
          ? res.value
          : null;
      return result?.then ? result.then(valueOf) : valueOf(result);
    },

    memoryClear() {
      this.memory = null;
      save(MEMORY_KEY, this.memory);
    },

    memoryRecall() {
      if (this.memory !== null)
        this.setInput(formatLite(this.memory, this.precision));
    },

    memoryAdd() {
      const value = this.memoryValue();
      const apply = (resolved) => {
        if (resolved === null) return;
        this.memory = (this.memory ?? 0) + resolved;
        save(MEMORY_KEY, this.memory);
      };
      if (value?.then) value.then(apply);
      else apply(value);
    },

    memorySubtract() {
      const value = this.memoryValue();
      const apply = (resolved) => {
        if (resolved === null) return;
        this.memory = (this.memory ?? 0) - resolved;
        save(MEMORY_KEY, this.memory);
      };
      if (value?.then) value.then(apply);
      else apply(value);
    },

    toggleTheme() {
      this.prefs.theme = this.prefs.theme === 'dark' ? 'light' : 'dark';
      save(PREFS_KEY, this.prefs);
    },

    toggleScientific() {
      this.prefs.scientific = !this.prefs.scientific;
      save(PREFS_KEY, this.prefs);
    },

    toggleAngleMode() {
      const modes = ['DEG', 'RAD', 'GRAD'];
      const index = modes.indexOf(this.angleMode);
      this.prefs.angleMode = modes[(index + 1) % modes.length];
      save(PREFS_KEY, this.prefs);
      this.updatePreview();
    },

    setTheme(theme) {
      if (theme !== 'dark' && theme !== 'light') return;
      this.prefs.theme = theme;
      save(PREFS_KEY, this.prefs);
    },

    setPrecision(precision) {
      const value = Number(precision);
      if (![8, 12, 16].includes(value)) return;
      this.prefs.precision = value;
      save(PREFS_KEY, this.prefs);
      this.updatePreview();
    },
    setAngleMode(mode) {
      if (!['DEG', 'RAD', 'GRAD'].includes(mode)) return;
      this.prefs.angleMode = mode;
      save(PREFS_KEY, this.prefs);
      this.updatePreview();
    },

    resetAllData() {
      this.history = [];
      this.variables = {};
      this.memory = null;
      this.prefs = { ...DEFAULT_PREFS };
      this.input = '';
      this.preview = '';
      this.result = '';
      this.error = '';
      this.lastValue = null;
      save(HISTORY_KEY, this.history);
      save(VARS_KEY, this.variables);
      save(PREFS_KEY, this.prefs);
      save(MEMORY_KEY, this.memory);
    },

    setPanel(panel) {
      this.prefs.panel = this.prefs.panel === panel ? null : panel;
      save(PREFS_KEY, this.prefs);
    },

    setVariable(name, value) {
      this.variables[name] = value;
      save(VARS_KEY, this.variables);
    },

    clearVariables() {
      this.variables = {};
      save(VARS_KEY, this.variables);
    },
  },
});

export { formatLite as format };
